'use client';

import React, { useMemo, useEffect } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useEstimator } from '../../context/EstimatorContext';

const ProjectQuantitiesSummary = () => {
  const { formData, budgetSummaryRows, setQuantitiesSummaryRows } = useEstimator();

  const getNumber = (val) => parseFloat(val ?? '') || 0;

  const rowData = useMemo(() => {
    const pipeRolledCoated = getNumber(formData?.quantities?.find((item) => item.rowNumber === 27)?.quantity);
    const constructionMats = getNumber(formData?.quantities?.find((item) => item.rowNumber === 33)?.quantity);
    const hdd = getNumber(formData?.quantities?.find((item) => item.rowNumber === 20)?.quantity);
    const wetLands = getNumber(formData?.quantities?.find((item) => item.rowNumber === 31)?.quantity);

    const totalTonsValue = formData.pipeSpecs.reduce((sum, row) => {
      const tons = parseFloat(row?.tons ?? 0);
      return sum + (isNaN(tons) ? 0 : tons);
    }, 0);

    const linePipeBorePipe = getNumber(budgetSummaryRows.find((item) => item.no === 6)?.rawPrice);

    const numberOfBores = formData?.quantities
      ?.filter((item) => [13, 15, 17].includes(item.rowNumber))
      .reduce((sum, item) => sum + getNumber(item.quantity), 0);

    const landOwnerAndROW =
      getNumber(budgetSummaryRows.find((item) => item.no === 1)?.rawPrice) +
      getNumber(budgetSummaryRows.find((item) => item.no === 2)?.rawPrice);

    const length2d = getNumber(formData?.quantities?.find((item) => item.rowNumber === 3)?.quantity);
    const diameter = getNumber(formData?.quantities?.find((item) => item.rowNumber === 1)?.quantity);

    const rowRod = length2d
      ? (landOwnerAndROW / (length2d * 0.0606061)).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '-';

    const constructionLabor = getNumber(budgetSummaryRows.find((item) => item.no === 13)?.rawPrice);
    const projectBaseCost = getNumber(budgetSummaryRows.find((item) => item.no === 26)?.rawPrice);

    const length3dFt = getNumber(formData?.quantities?.find((item) => item.rowNumber === 6)?.quantity);
    const length3dMiles = getNumber(formData?.quantities?.find((item) => item.rowNumber === 7)?.quantity);

    const baseLay = length3dFt
      ? (constructionLabor / length3dFt).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '-';

    const pricePerTon = totalTonsValue
      ? (linePipeBorePipe / totalTonsValue).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '-';

    const constructionPerFt = diameter
      ? (constructionLabor / length3dFt).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '-';

    const pricePerMile = diameter
      ? (projectBaseCost / diameter / length3dMiles).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '-';

    return [
      { no: 1, quantityType: 'Foreign Line Crossings', quantity: `${pipeRolledCoated} EA` },
      { no: 2, quantityType: 'Construction Mats', quantity: `${constructionMats} EA` },
      { no: 3, quantityType: 'Number of Bores', quantity: `${numberOfBores} EA` },
      { no: 4, quantityType: 'Horizontal Directional Drill', quantity: `${hdd} LF` },
      { no: 5, quantityType: 'Wetlands & Waterbodies', quantity: `${wetLands} LF` },
      { no: 6, quantityType: 'ROW $/Rod', quantity: `$${rowRod}` },
      { no: 7, quantityType: 'Base Lay $/ft', quantity: `$${baseLay}` },
      { no: 8, quantityType: 'Total Tonnage', quantity: totalTonsValue },
      { no: 9, quantityType: '$/ton', quantity: `$${pricePerTon}` },
      { no: 10, quantityType: 'Construction $/ft', quantity: `$${constructionPerFt}` },
      { no: 11, quantityType: '$/in-mile', quantity: `$${pricePerMile}` },
    ];
  }, [formData, budgetSummaryRows]);

  useEffect(() => {
    setQuantitiesSummaryRows(rowData);
  }, [rowData, setQuantitiesSummaryRows]);

  const projectQuantitiesColumns = [
    { headerName: 'Quantity Type', field: 'quantityType', flex: 2, cellStyle: { fontWeight: 500, fontSize: '14px' } },
    { headerName: 'Quantity', field: 'quantity', flex: 1, cellStyle: { fontSize: '14px' } },
  ];

  return (
    <Paper
      elevation={4}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        p: 0,
        backgroundColor: '#f9f9f9',
        border: '1px solid #e0e0e0',
      }}
    >
      {/* Title */}
      <Box
        sx={{
          backgroundColor: '#223A64',
          py: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '18px' }}>
          Project Quantities
        </Typography>
      </Box>

      {/* Table */}
      <Box
        className="ag-theme-alpine"
        sx={{
          width: '100%',
          '& .ag-root-wrapper': { border: 'none' },
          '& .ag-header': { display: 'none' },
          '& .ag-row': { borderBottom: '1px solid #eee' },
          '& .ag-cell': { display: 'flex', alignItems: 'center' },
        }}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={projectQuantitiesColumns}
          headerHeight={0}
          domLayout="autoHeight"
          suppressMenuHide
          suppressCellFocus
          suppressMovableColumns
          suppressColumnVirtualisation
          defaultColDef={{
            resizable: true,
            sortable: false,
            filter: false,
          }}
        />
      </Box>
    </Paper>
  );
};

export default ProjectQuantitiesSummary;
